import { X } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type MediaLightboxProps = {
	isOpen: boolean;
	onClose: () => void;
	/** Obrigatório quando `children` não é passado (imagem ou vídeo nativo). */
	src?: string;
	alt?: string;
	/** "image" cobre tambem GIF; "video" usa <video controls autoPlay loop>. */
	kind?: 'image' | 'video';
	/** Conteúdo customizado (ex.: embed YouTube). Quando definido, ignora `src` e `kind`. */
	children?: ReactNode;
	className?: string;
};

const MediaLightbox = ({
	isOpen,
	onClose,
	src = '',
	alt,
	kind = 'image',
	children,
	className,
}: MediaLightboxProps) => {
	useEffect(() => {
		if (!isOpen) return;

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', handleKeyDown);

		return () => {
			document.body.style.overflow = previousOverflow;
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label={alt || 'Pré-visualização'}
			className={cn(
				'fixed inset-0 z-[10001] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm md:p-8',
				className,
			)}
			onClick={onClose}
		>
			<button
				type="button"
				onClick={(event) => {
					event.stopPropagation();
					onClose();
				}}
				aria-label="Fechar"
				className="absolute right-4 top-4 z-10 inline-flex size-11 items-center justify-center rounded-full bg-white/30 text-white backdrop-blur transition-colors hover:bg-white/40 md:right-6 md:top-6"
			>
				<X className="h-6 w-6" />
			</button>

			{/* Sem w-full: senão a área “vazia” ao redor da imagem (letterbox) engole o clique e não fecha. */}
			<div
				className="relative inline-flex max-h-[80vh] max-w-[80vw] items-center justify-center"
				onClick={(event) => event.stopPropagation()}
			>
				{children ? (
					children
				) : kind === 'video' ? (
					<video
						src={src}
						className="max-h-[80vh] max-w-[80vw] rounded-2xl shadow-2xl"
						controls
						autoPlay
						loop
						playsInline
					/>
				) : (
					<img
						src={src}
						alt={alt || ''}
						className="max-h-[80vh] max-w-[80vw] rounded-2xl object-contain shadow-2xl"
					/>
				)}
			</div>
		</div>
	);
};

export { MediaLightbox };
